import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { useSelector, useDispatch } from "react-redux";
import withRouter from "../../Components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { resetLoginFlag } from "../../slices/thunks";
import { createSelector } from "reselect";
import {
  adminGetAdminMenus,
  adminGetCompanySettingById,
  getToken,
  getTokenSuperadmin,
  postLogin,
  adminChangePasswordFirstLogin,
} from "helpers/backend_helpers";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import LoginForm from "./LoginForm";
import ChangePasswordForm from "./ChangePasswordForm";

const Login = (props: any) => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const selectLayoutState = (state: any) => state;
  const loginpageData = createSelector(selectLayoutState, (state) => ({
    user: state.Account.user,
    error: state.Login.error,
    loading: state.Login.loading,
    errorMsg: state.Login.errorMsg,
  }));

  const { error, errorMsg } = useSelector(loginpageData);

  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [confirmPasswordShow, setConfirmPasswordShow] =
    useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [firstLogin, setFirstLogin] = useState(false);
  const [savedUsername, setSavedUsername] = useState<string>("");

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Username belum diisi"),
      password: Yup.string().required("Password belum diisi"),
    }),
    onSubmit: async (values) => {
      if (loader) return;

      setLoader(true);

      try {
        sessionStorage.removeItem("authUser");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("userRole");
        sessionStorage.removeItem("menu");
        sessionStorage.removeItem("companyId");
        sessionStorage.removeItem("currentCompany");

        const loginResponse: any = await postLogin(values);

        if (!loginResponse?.access_token) {
          throw new Error("Token tidak ditemukan");
        }

        sessionStorage.setItem(
          "authUser",
          JSON.stringify(loginResponse.access_token),
        );

        setSavedUsername(values.username);

        await getUserData(values.username);
      } catch (error: any) {
        console.error("Login error:", error);
        toast.error(error?.message || "Login gagal!");
        setLoader(false);
      }
    },
  });

  const validationChangePassword = useFormik({
    enableReinitialize: true,
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required("Password baru belum diisi")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          "Password harus minimal 8 karakter, mengandung huruf besar, huruf kecil, angka, dan karakter khusus (@$!%*?&)",
        ),
      confirmPassword: Yup.string()
        .required("Konfirmasi password belum diisi")
        .oneOf([Yup.ref("newPassword")], "Password tidak sama"),
    }),
    onSubmit: async (values) => {
      if (loader) return;

      setLoader(true);
      try {
        await adminChangePasswordFirstLogin({
          password: values.newPassword,
        });

        toast.success("Password berhasil diubah! Silakan login kembali.");

        sessionStorage.removeItem("authUser");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("userRole");
        sessionStorage.removeItem("menu");
        sessionStorage.removeItem("companyId");
        sessionStorage.removeItem("currentCompany");

        setFirstLogin(false);

        validation.setFieldValue("username", savedUsername);
        validation.setFieldValue("password", "");

        validationChangePassword.resetForm();
      } catch (error: any) {
        console.error("Change password error:", error);
        toast.error(error?.message || "Gagal mengubah password!");
      } finally {
        setLoader(false);
      }
    },
  });

  const getUserData = async (username: string) => {
    try {
      if (username === "superadmin") {
        const response = await getTokenSuperadmin();

        if (!response) {
          throw new Error("Gagal mendapatkan data superadmin");
        }

        sessionStorage.setItem("user", JSON.stringify(response));
        sessionStorage.setItem("userRole", "superadmin");

        toast.success("Login Berhasil sebagai Superadmin");
        setLoader(false);
        navigate("/superadmin/dashboard");
      } else {
        const response: any = await getToken();

        if (!response) {
          throw new Error("Gagal mendapatkan data user");
        }

        sessionStorage.setItem("user", JSON.stringify(response));
        sessionStorage.setItem("userRole", "admin");

        const menusResponse: any = await adminGetAdminMenus();
        const baseMenus = [...menusResponse.map((item: any) => item.key)];

        const companySetting: any = await adminGetCompanySettingById();
        if (companySetting.allowCheckPointPatrol === true) {
          baseMenus.push("PATROL_LOCATION");
        }

        sessionStorage.setItem("menu", JSON.stringify(baseMenus));
        sessionStorage.setItem("companyId", response.companyId);

        if (response.isFirstLogin) {
          setFirstLogin(true);
          setLoader(false);
          toast.info("Silakan ubah password Anda terlebih dahulu");
        } else {
          toast.success("Login Berhasil!");
          setLoader(false);
          navigate("/dashboard");
        }
      }
    } catch (error: any) {
      console.error("Get user data error:", error);
      toast.error(error?.message || "Gagal mengambil data user!");
      setLoader(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;

    if (strength <= 2) return { text: "Lemah", color: "danger", width: "33%" };
    if (strength <= 4)
      return { text: "Sedang", color: "warning", width: "66%" };
    return { text: "Kuat", color: "success", width: "100%" };
  };

  const passwordStrength = getPasswordStrength(
    validationChangePassword.values.newPassword,
  );

  useEffect(() => {
    if (error || errorMsg) {
      setTimeout(() => {
        dispatch(resetLoginFlag());
        setLoader(false);
      }, 3000);
    }
  }, [dispatch, error, errorMsg]);

  document.title = "Lima Utama Jaya";

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <div className="d-inline-block"></div>
                  </div>
                  <p className="mt-3 fs-15 fw-medium"></p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card style={{ marginTop: "5rem" }}>
                  <CardBody className="p-4">
                    {firstLogin ? (
                      <ChangePasswordForm
                        validationChangePassword={validationChangePassword}
                        passwordShow={passwordShow}
                        setPasswordShow={setPasswordShow}
                        confirmPasswordShow={confirmPasswordShow}
                        setConfirmPasswordShow={setConfirmPasswordShow}
                        loader={loader}
                        passwordStrength={passwordStrength}
                      />
                    ) : (
                      <LoginForm
                        validation={validation}
                        passwordShow={passwordShow}
                        setPasswordShow={setPasswordShow}
                        loader={loader}
                        error={error}
                      />
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Login);
